import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { reportPdfUrl } from "@/lib/api";
import { formatIdDate } from "@/lib/format";
import PickupWidget from "@/components/PickupWidget";
import FraudAttemptsWidget from "@/components/FraudAttemptsWidget";
import SortableWidget from "@/components/SortableWidget";
import { Switch } from "@/components/ui/switch";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText,
  CheckCircle,
  Clock,
  Warning,
  ShieldCheck,
  ArrowRight,
  FilePdf,
  Gear,
  ArrowsClockwise,
  CalendarBlank,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { devWarn } from "@/lib/debug";

function StatCard({ label, value, sublabel, icon: Icon, accent, testId, to }) {
  // Colored soft-bg circle per mockup: light tint + darker icon.
  const bubbleCls = accent === "primary"
    ? "bg-orange-100 text-primary"
    : accent === "success"
    ? "bg-emerald-100 text-emerald-700"
    : accent === "warning"
    ? "bg-amber-100 text-amber-700"
    : accent === "danger"
    ? "bg-red-100 text-red-700"
    : accent === "info"
    ? "bg-blue-100 text-blue-700"
    : "bg-secondary text-muted-foreground";
  const inner = (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="text-4xl font-black tracking-tight mt-3 font-mono leading-none">{value}</div>
          {sublabel && (
            <div className="text-xs text-muted-foreground mt-3 truncate">{sublabel}</div>
          )}
        </div>
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${bubbleCls}`}>
          <Icon size={20} weight="bold" />
        </div>
      </div>
    </>
  );
  const base = "rounded-xl border border-border p-5 bg-card block transition-all";
  if (to) {
    return (
      <Link
        to={to}
        className={`${base} hover:shadow-md hover:border-primary/40 cursor-pointer`}
        data-testid={testId}
        aria-label={`Lihat ${label}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="border border-border p-5 bg-card rounded-xl" data-testid={testId}>
      {inner}
    </div>
  );
}

const WIDGETS = [
  { key: "kpi", label: "KPI Cards" },
  { key: "report", label: "Laporan PDF" },
  { key: "fraud", label: "Anti-Fraud (SPB Palsu)" },
  { key: "pickup", label: "Widget Pengambilan" },
  { key: "charts", label: "Grafik Distrik & Produk" },
  { key: "recent", label: "SPB Terakhir" },
];

const DEFAULT_ORDER = WIDGETS.map((w) => w.key);
const DEFAULT_VISIBLE = { kpi: true, report: true, fraud: true, pickup: true, charts: true, recent: true };

// Static: recharts <Tooltip> content styling. Hoisted so we don't re-create
// the object on every Dashboard render.
const TOOLTIP_CONTENT_STYLE = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 0,
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [rangeFrom, setRangeFrom] = useState(monthStart);
  const [rangeTo, setRangeTo] = useState(today);

  // Visible widgets (persisted in localStorage per user/browser)
  const [visible, setVisible] = useState(() => {
    try {
      const raw = localStorage.getItem("spb_dashboard_widgets");
      if (raw) return { ...DEFAULT_VISIBLE, ...JSON.parse(raw) };
    } catch (err) { devWarn("Failed to read widget visibility from localStorage", err); }
    return DEFAULT_VISIBLE;
  });

  // Widget order (persisted). Ensures new widgets appended, unknown keys dropped.
  const [order, setOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("spb_dashboard_order");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter((k) => DEFAULT_ORDER.includes(k));
          const missing = DEFAULT_ORDER.filter((k) => !filtered.includes(k));
          return [...filtered, ...missing];
        }
      }
    } catch (err) { devWarn("Failed to read widget order from localStorage", err); }
    return DEFAULT_ORDER;
  });

  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("spb_dashboard_widgets", JSON.stringify(visible));
    } catch (err) { devWarn("Failed to persist widget visibility", err); }
  }, [visible]);

  useEffect(() => {
    try {
      localStorage.setItem("spb_dashboard_order", JSON.stringify(order));
    } catch (err) { devWarn("Failed to persist widget order", err); }
  }, [order]);

  const toggleWidget = (key) => setVisible((v) => ({ ...v, [key]: !v[key] }));
  const resetOrder = () => setOrder(DEFAULT_ORDER);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrder((current) => {
      const oldIndex = current.indexOf(active.id);
      const newIndex = current.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return current;
      return arrayMove(current, oldIndex, newIndex);
    });
  };

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/summary"),
      api.get("/spb?limit=10"),
    ])
      .then(([s, r]) => {
        setSummary(s.data);
        setRecent(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const widgetLabel = useMemo(
    () => Object.fromEntries(WIDGETS.map((w) => [w.key, w.label])),
    []
  );

  const visibleOrder = useMemo(
    () => order.filter((k) => visible[k]),
    [order, visible]
  );

  if (loading || !summary) {
    return (
      <div className="p-8">
        <div className="overline">Memuat dashboard...</div>
      </div>
    );
  }

  const renderWidget = (key) => {
    if (!visible[key]) return null;
    switch (key) {
      case "kpi":
        return (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="widget-kpi-content">
            <StatCard label="Total SPB" value={summary.total} sublabel="Semua periode" icon={FileText} accent="primary" testId="stat-total" to="/spb" />
            <StatCard label="Sudah Termuat" value={summary.termuat} sublabel={summary.total > 0 ? `${Math.round((summary.termuat / summary.total) * 100)}% dari total SPB` : "-"} icon={CheckCircle} accent="success" testId="stat-termuat" to="/spb?status=sudah_termuat" />
            <StatCard label="Belum Termuat" value={summary.belum_termuat} sublabel={summary.total > 0 ? `${Math.round((summary.belum_termuat / summary.total) * 100)}% dari total SPB` : "-"} icon={Clock} accent="info" testId="stat-belum" to="/spb?status=belum_termuat" />
            <StatCard label="Mismatch" value={summary.mismatch} sublabel="Perlu perhatian" icon={Warning} accent="danger" testId="stat-mismatch" to="/spb?status=mismatch" />
            <StatCard label="Diverifikasi" value={summary.diverifikasi} sublabel="Menunggu verifikasi" icon={ShieldCheck} accent="warning" testId="stat-diverifikasi" to="/spb?status=diverifikasi" />
          </div>
        );
      case "report":
        return (
          <div className="border border-border rounded-xl p-4 bg-card" data-testid="report-panel">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Laporan SPB PDF</div>
                <p className="text-xs text-muted-foreground mt-1">Pilih periode lalu unduh laporan agregat.</p>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">Dari</label>
                  <Input
                    type="date"
                    value={rangeFrom}
                    onChange={(e) => setRangeFrom(e.target.value)}
                    className="rounded-lg h-10"
                    data-testid="report-from"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">Sampai</label>
                  <Input
                    type="date"
                    value={rangeTo}
                    onChange={(e) => setRangeTo(e.target.value)}
                    className="rounded-lg h-10"
                    data-testid="report-to"
                  />
                </div>
              </div>
              <a
                href={reportPdfUrl({ tanggal_from: rangeFrom, tanggal_to: rangeTo })}
                target="_blank"
                rel="noreferrer"
                data-testid="download-report-pdf"
              >
                <Button className="rounded-lg h-10 font-bold uppercase tracking-wider">
                  <FilePdf size={16} className="mr-2" /> Download PDF
                </Button>
              </a>
            </div>
          </div>
        );
      case "pickup":
        return <PickupWidget />;
      case "fraud":
        return <FraudAttemptsWidget />;
      case "charts":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="widget-charts-content">
            <div className="lg:col-span-2 border border-border p-6 bg-card">
              <div className="overline mb-1">Rekap Per Distrik</div>
              <h3 className="font-bold text-lg tracking-tight mb-4">Distribusi SPB</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={summary.per_distrik.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="kode" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
                  <Bar dataKey="total" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-border p-6 bg-card">
              <div className="overline mb-1">Rekap Per Produk</div>
              <h3 className="font-bold text-lg tracking-tight mb-4">Volume Produk</h3>
              <div className="space-y-4">
                {summary.per_produk.map((p) => {
                  const maxT = Math.max(...summary.per_produk.map((x) => x.total), 1);
                  const pct = Math.round((p.total / maxT) * 100);
                  return (
                    <div key={p.nama}>
                      <div className="flex justify-between mb-1 text-sm">
                        <span className="font-semibold">{p.nama}</span>
                        <span className="font-mono font-bold">{p.total}</span>
                      </div>
                      <div className="h-2 bg-secondary">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                {summary.per_produk.length === 0 && (
                  <div className="text-sm text-muted-foreground">Belum ada data.</div>
                )}
              </div>
            </div>
          </div>
        );
      case "recent":
        return (
          <div className="border border-border bg-card" data-testid="widget-recent-content">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div>
                <div className="overline">Aktivitas Terbaru</div>
                <h3 className="font-bold text-lg tracking-tight">10 SPB Terakhir</h3>
              </div>
              <Link to="/spb" className="text-sm font-semibold text-primary flex items-center gap-1" data-testid="view-all-spb">
                Lihat Semua <ArrowRight size={14} />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-left">
                    <th className="p-3 overline">No SPB</th>
                    <th className="p-3 overline">Tanggal</th>
                    <th className="p-3 overline">Distrik</th>
                    <th className="p-3 overline">Produk</th>
                    <th className="p-3 overline">No Polisi</th>
                    <th className="p-3 overline">Jumlah</th>
                    <th className="p-3 overline">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((s) => (
                    <tr key={s.id} className="border-b border-border hover:bg-secondary/50">
                      <td className="p-3 font-mono font-bold">
                        <Link to={`/spb/${s.id}`} className="hover:underline">
                          {s.no_spb}
                        </Link>
                      </td>
                      <td className="p-3 font-mono">{formatIdDate(s.tanggal)}</td>
                      <td className="p-3">{s.distrik_kode} ({s.distrik_nama})</td>
                      <td className="p-3">{s.produk_nama}</td>
                      <td className="p-3 font-mono font-bold">{s.no_polisi}</td>
                      <td className="p-3 font-mono">{s.jumlah} {s.satuan}</td>
                      <td className="p-3"><StatusPill status={s.status} /></td>
                    </tr>
                  ))}
                  {recent.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground">
                        Belum ada SPB. <Link to="/spb/new" className="text-primary font-semibold">Buat SPB pertama →</Link>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      {/* Header — mockup: pill-badge label + big heading + subtitle + calendar chip;
          right side: outline Atur Widget + orange BUAT SPB BARU */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-8 gap-4">
        <div>
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-orange-100 text-primary px-2.5 py-1 rounded" data-testid="dashboard-panel-badge">
            Panel Admin
          </span>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-3">Dashboard</h1>
          <p className="text-muted-foreground mt-2 text-sm">Monitoring Surat Pengambilan Barang</p>
          <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground bg-secondary/60 rounded-md px-3 py-1.5 w-fit">
            <CalendarBlank size={14} weight="bold" />
            <span>{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={editMode ? "default" : "outline"}
            className="rounded-lg h-11"
            onClick={() => setEditMode((e) => !e)}
            data-testid="dashboard-edit-mode"
          >
            <Gear size={16} className="mr-2" /> {editMode ? "Selesai" : "Atur Widget"}
          </Button>
          <Link to="/spb/new">
            <Button className="rounded-lg h-11 font-bold uppercase tracking-wider" data-testid="create-spb-button">
              <FileText size={16} className="mr-2" /> Buat SPB Baru
            </Button>
          </Link>
        </div>
      </div>

      {editMode && (
        <div className="border border-primary/40 bg-primary/5 p-4 mb-8 space-y-3" data-testid="widget-toggles">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="overline">Mode Ubah Layout</div>
              <p className="text-xs text-muted-foreground mt-1">
                Seret pegangan <span className="font-bold">⋮⋮ label widget</span> untuk mengubah urutan · Toggle di bawah untuk sembunyikan/tampilkan.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none h-9"
              onClick={resetOrder}
              data-testid="reset-widget-order"
            >
              <ArrowsClockwise size={14} className="mr-2" /> Reset Urutan
            </Button>
          </div>
          <div className="flex flex-wrap gap-4 pt-2 border-t border-primary/20">
            {WIDGETS.map((w) => (
              <label key={w.key} className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch
                  checked={!!visible[w.key]}
                  onCheckedChange={() => toggleWidget(w.key)}
                  data-testid={`toggle-widget-${w.key}`}
                />
                <span>{w.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleOrder} strategy={verticalListSortingStrategy}>
          <div className="space-y-8" data-testid="dashboard-widget-list">
            {visibleOrder.map((key) => (
              <SortableWidget key={key} id={key} editMode={editMode} label={widgetLabel[key]}>
                {renderWidget(key)}
              </SortableWidget>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
